import React, { useState, useContext } from 'react';
import { FaTimes, FaTags } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import styles from './TokenPurchaseModal.module.css';
import AuthContext from '../../context/AuthContext';
import { createRazorpayOrder, verifyRazorpayPayment, initializeRazorpayPayment } from '../../services/paymentApi';
import StarYellowIcon from '../../assets/dashboard_assets/star_yellow.png';

const TokenPurchaseModal = ({ show, onClose }) => {
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [customAmount, setCustomAmount] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('packages'); // 'packages' or 'custom'
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();

    // Package options (up to 500-1000 tokens)
    const packages = [
        {
            id: 'starter',
            tokens: 100,
            originalPrice: 600,
            discountedPrice: 400,
            popular: false
        },
        {
            id: 'popular',
            tokens: 300,
            originalPrice: 1800,
            discountedPrice: 1200,
            popular: true
        },
        {
            id: 'bulk',
            tokens: 500,
            originalPrice: 3000,
            discountedPrice: 2000,
            popular: false
        }
    ];

    const calculateCustomPrice = (tokens) => {
        const originalPrice = tokens * 6;
        const discountedPrice = tokens * 4;
        return { originalPrice, discountedPrice };
    };

    const handlePackageSelect = (pkg) => {
        setSelectedPackage(pkg);
        setCustomAmount('');
    };

    const handleCustomAmountChange = (e) => {
        const value = e.target.value;
        if (value === '' || (/^\d+$/.test(value) && parseInt(value) <= 1000)) {
            setCustomAmount(value);
            setSelectedPackage(null);
        }
    };

    const handlePurchase = async () => {
        if (!authCtx.token) {
            alert('Please login to purchase tokens');
            return;
        }

        setIsLoading(true);

        try {
            let tokensToAdd, amountToPay;

            if (selectedPackage) {
                tokensToAdd = selectedPackage.tokens;
                amountToPay = selectedPackage.discountedPrice;
            } else if (customAmount) {
                tokensToAdd = parseInt(customAmount);
                const pricing = calculateCustomPrice(tokensToAdd);
                amountToPay = pricing.discountedPrice;
            } else {
                alert('Please select a package or enter a custom amount');
                setIsLoading(false);
                return;
            }

            // Create Razorpay order
            const orderResponse = await createRazorpayOrder(
                authCtx.token,
                amountToPay,
                'tokens',
                { tokens: tokensToAdd }
            );

            if (!orderResponse.success) {
                throw new Error(orderResponse.message || 'Failed to create order');
            }

            // Initialize Razorpay payment
            await initializeRazorpayPayment(
                orderResponse.order,
                async (paymentResponse) => {
                    try {
                        // Verify payment with backend
                        const verifyResponse = await verifyRazorpayPayment(
                            authCtx.token,
                            paymentResponse.razorpay_order_id,
                            paymentResponse.razorpay_payment_id,
                            paymentResponse.razorpay_signature
                        );

                        if (verifyResponse.success) {
                            // Update user tokens in context with the actual balance from backend
                            if (authCtx.updateUserTokens && verifyResponse.user_balance) {
                                authCtx.updateUserTokens(verifyResponse.user_balance.tokens);
                            }

                            // Close modal and redirect to deal tags
                            onClose();
                            navigate('/dashboard/tools/check-deal-tags', {
                                state: {
                                    message: `Successfully purchased ${tokensToAdd} tokens! Your new balance: ${verifyResponse.user_balance?.tokens || 0} tokens`,
                                    type: 'success'
                                }
                            });
                        } else {
                            throw new Error('Payment verification failed');
                        }
                    } catch (error) {
                        alert('Payment verification failed. Please contact support if amount was deducted.');
                    }
                },
                (error) => {
                    if (error !== 'Payment cancelled by user') {
                        alert('Payment failed. Please try again.');
                    }
                }
            );

        } catch (error) {
            alert(error.message || 'Failed to purchase tokens. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };


    const getSelectedDetails = () => {
        if (selectedPackage) {
            return {
                tokens: selectedPackage.tokens,
                originalPrice: selectedPackage.originalPrice,
                discountedPrice: selectedPackage.discountedPrice
            };
        }
        if (customAmount) {
            const pricing = calculateCustomPrice(parseInt(customAmount));
            return {
                tokens: parseInt(customAmount),
                originalPrice: pricing.originalPrice,
                discountedPrice: pricing.discountedPrice
            };
        }
        return null;
    };

    const selectedDetails = getSelectedDetails();

    if (!show) return null;

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>
                        <img src={StarYellowIcon} alt="Star" className={styles.titleIcon} />
                        Buy Tokens
                    </h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                {/* Tabs */}
                <div className={styles.tabs}>
                    <button
                        className={`${styles.tab} ${activeTab === 'packages' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('packages')}
                    >
                        Packages
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'custom' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('custom')}
                    >
                        Custom Amount
                    </button>
                </div>

                <div className={styles.modalBody}>
                    {activeTab === 'packages' && (
                        <div className={styles.packagesGrid}>
                            {packages.map((pkg) => (
                                <div
                                    key={pkg.id}
                                    className={`${styles.packageCard} ${selectedPackage?.id === pkg.id ? styles.selected : ''} ${pkg.popular ? styles.popular : ''}`}
                                    onClick={() => handlePackageSelect(pkg)}
                                >
                                    {pkg.popular && (
                                        <div className={styles.popularBadge}>Most Popular</div>
                                    )}
                                    <div className={styles.packageTokens}>{pkg.tokens} Tokens</div>
                                    <div className={styles.packagePricing}>
                                        <span className={styles.originalPackagePrice}>₹{pkg.originalPrice}</span>
                                        <span className={styles.discountedPackagePrice}>₹{pkg.discountedPrice}</span>
                                    </div>
                                    <div className={styles.savings}>
                                        Save ₹{pkg.originalPrice - pkg.discountedPrice}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {activeTab === 'custom' && (
                        <div className={styles.customSection}>
                            <div className={styles.inputGroup}>
                                <label className={styles.inputLabel}>Number of Tokens (Max: 1000)</label>
                                <input
                                    type="text"
                                    value={customAmount}
                                    onChange={handleCustomAmountChange}
                                    placeholder="Enter number of tokens"
                                    className={styles.customInput}
                                />
                            </div>
                            {customAmount && (
                                <div className={styles.customPricing}>
                                    <div className={styles.customCalculation}>
                                        <span className={styles.customOriginal}>₹{calculateCustomPrice(parseInt(customAmount)).originalPrice}</span>
                                        <span className={styles.customDiscounted}>₹{calculateCustomPrice(parseInt(customAmount)).discountedPrice}</span>
                                    </div>
                                    <div className={styles.customSavings}>
                                        You save ₹{calculateCustomPrice(parseInt(customAmount)).originalPrice - calculateCustomPrice(parseInt(customAmount)).discountedPrice}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {selectedDetails && (
                    <div className={styles.selectedSummary}>
                        <div className={styles.summaryItem}>
                            <span>Tokens:</span>
                            <span>{selectedDetails.tokens}</span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span>Total:</span>
                            <span>
                                <span className={styles.summaryOriginal}>₹{selectedDetails.originalPrice}</span>
                                <span className={styles.summaryDiscounted}>₹{selectedDetails.discountedPrice}</span>
                            </span>
                        </div>
                    </div>
                )}

                {/* Pricing Info */}
                <div className={styles.pricingInfo}>
                    <div className={styles.discount}>
                        <FaTags className={styles.discountIcon} />
                        <span className={styles.originalPrice}>₹6</span>
                        <span className={styles.discountedPrice}>₹4</span>
                        <span className={styles.discountText}>per token</span>
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <button
                        className={styles.cancelButton}
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                    <button
                        className={styles.purchaseButton}
                        onClick={handlePurchase}
                        disabled={!selectedDetails || isLoading}
                    >
                        {isLoading ? 'Processing...' : 'Purchase Tokens'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TokenPurchaseModal;